import { Box, Button, ClickAwayListener, IconButton } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Cancel } from '@mui/icons-material';
import React from 'react';
import propTypes from 'prop-types';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import useChat from '../../hooks/useChat';
import { useFirebase, useFirestore } from 'react-redux-firebase';
import { actions } from '../../actions';
import collections from '../../constants/FirestoreCollections';

const useStyles = makeStyles((theme) => ({
  main: {
    backgroundColor: 'rgba(0,0,0,.5)',
    paddingTop: 4,
    zIndex: 1000000,
    position: 'fixed',
    width: '100%',
    height: '100%',
    top: 0,
    left: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  body: {
    backgroundColor: theme.palette.white.main,
    minWidth: '300px',
    borderRadius: 8,
    padding: 4,
  },
}));

function TemplatesPopup(props) {
  const { open, handleClose } = props;
  const classes = useStyles();
  const templatesSent = useSelector((state) => state.app.templatesSent) || [];
  const chatId = useSelector((state) => state.app.activeChat);
  const chat = useChat();
  const organization = useSelector((state) => state.app.organization);
  const appDispatch = useDispatch();
  const firebase = useFirebase();
  const profile = useSelector((state) => state.firebase.profile);
  const firestore = useFirestore();

  const handleSendTemplate = async (templateName) => {
    try {
      const findedPhone = templatesSent.find((e) => {
        if (e.phone === chat.client.phone.replace('+', '')) {
          const date = new Date(e.date);
          const yesterday = moment().subtract(1, 'day').toDate();
          if (date > yesterday && e.orgId === organization.id && e.type === templateName) {
            return true;
          }
        }
        return false;
      });
      if (findedPhone) {
        appDispatch({
          type: actions.alert,
          alert: {
            snackbarOpen: true,
            snackbarMessage: 'Ya no puede enviar mas plantillas de este tipo el día de hoy',
            snackbarSeverity: 'error',
          },
        });
      } else {
        if (chat.botStopped) {
          firestore.collection(collections.chats).doc(chatId).update({
            botStopped: false,
          });
        }

        const templatePhone = {
          phone: chat.client.phone.replace('+', ''),
          date: Date.now(),
          type: templateName,
          orgId: organization.id,
        };
        firestore
          .collection(collections.configurations)
          .where('organizationId', '==', organization.id)
          .where('name', '==', 'notifications')
          .get()
          .then((res) => {
            const id = res.docs[0].id;
            appDispatch({
              type: actions.changeLoading,
              loading: { loading: true, priority: 1 },
            });
            const template = organization.templates.find((e) => e.name === templateName);
            const wrapperSendTemplateMessage = firebase
              .functions()
              .httpsCallable('wrapperSendTemplateMessage');
            wrapperSendTemplateMessage({
              templateId: template.id,
              clientId: organization.id,
              phone: chat.client.phone.replace('+', ''),
              msgId: '',
              email: profile.email,
              institutionId:
                (organization.healthSoftware && organization.healthSoftware.institutionId) || 0,
              subsidiaryId:
                (organization.healthSoftware && organization.healthSoftware.subsidiaryId) || 0,
            }).then((res) => {
              const data = res.data;
              if (data.status === 200) {
                firestore
                  .collection(collections.configurations)
                  .doc(id)
                  .update({ templatesSent: [...(templatesSent || []), templatePhone] });

                appDispatch({
                  type: actions.alert,
                  alert: {
                    snackbarOpen: true,
                    snackbarMessage: `Notificación enviada correctamente!`,
                    snackbarSeverity: 'success',
                  },
                });
                appDispatch({
                  type: actions.changeLoading,
                  loading: { loading: false, priority: 1 },
                });
                appDispatch({
                  type: actions.templatesSent,
                  templates: [...templatesSent, templatePhone],
                });

                const newStatus = {
                  creationDate: firestore.Timestamp.fromDate(new Date()),
                  status: 3,
                  user: profile.email,
                };

                firestore
                  .collection(collections.chats)
                  .where('client.phone', '==', chat.client.phone.replace('+', ''))
                  .orderBy('updatedAt', 'desc')
                  .get()
                  .then((res) => {
                    if (!res.empty) {
                      const chats = [];
                      res.forEach((e) => chats.push({ id: e.id, ...e.data() }));
                      const chatsToChange = chats.filter((e) => e.status !== 3);
                      if (chatsToChange.length > 1) {
                        for (const chat of chatsToChange.slice(1)) {
                          firestore
                            .collection(collections.chats)
                            .doc(chat.id)
                            .update({
                              statusHistory: firestore.FieldValue.arrayUnion(newStatus),
                              status: 3,
                            });
                        }
                      }
                      if (chatsToChange) {
                        // open new chat
                        const id = chatsToChange[0].id;
                        appDispatch({ type: actions.activeChat, id });
                        handleClose();
                      }
                    }
                  });
              } else {
                appDispatch({
                  type: actions.changeLoading,
                  loading: false,
                });
                appDispatch({
                  type: actions.alert,
                  alert: {
                    snackbarOpen: true,
                    snackbarMessage: `Error al enviar${
                      data.status === 400 && ` (${data.message})`
                    }`,
                    snackbarSeverity: 'error',
                  },
                });
              }
            });
          });
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
      appDispatch({
        type: actions.alert,
        alert: {
          snackbarOpen: true,
          snackbarMessage: `Ocurrio un error en el envio de su notificación!`,
          snackbarSeverity: 'error',
        },
      });
      appDispatch({
        type: actions.changeLoading,
        loading: { loading: false, priority: 1 },
      });
    }
  };

  const templatesPhones = templatesSent
    ? templatesSent.filter(
        (e) =>
          chat &&
          chat.client &&
          chat.client.phone &&
          e.phone === chat.client.phone.replace('+', ''),
      )
    : [];

  const handleDisableButton = (name) => {
    const findedTemplate = templatesPhones.find((e) => {
      const date = new Date(e.date);
      const yesterday = moment().subtract(1, 'day').toDate();
      if (date > yesterday && e.type === name && e.orgId === organization.id) {
        return true;
      }
      return false;
    });
    return findedTemplate;
  };

  return (
    open && (
      <Box className={classes.main}>
        <ClickAwayListener onClickAway={handleClose}>
          <Box className={classes.body}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <IconButton onClick={handleClose} size="large">
                <Cancel />
              </IconButton>
              <Box mr={2}>
                <img src="/logo512.png" width="30" alt="PrestoBots" />
              </Box>
            </Box>
            {organization?.templates
              ?.filter((e) => e.isActive && Array.isArray(e.params) && e.params.length === 0)
              ?.map((e, index) => (
                <Box key={e.id || index} display="flex" flexDirection="column" my={1} mx={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleSendTemplate(e.name)}
                    disabled={handleDisableButton(e.name)}
                  >
                    {e.displayName}
                  </Button>
                </Box>
              ))}
          </Box>
        </ClickAwayListener>
      </Box>
    )
  );
}

const Props = {
  open: propTypes.bool,
  handleClose: propTypes.func,
};

TemplatesPopup.propTypes = Props;

export default TemplatesPopup;
